// Bundles /template-qa skill outputs from outputs/template-qa/ into the SPA at
// build time. Each time the user runs /template-qa locally and pushes the
// results, the dashboard surfaces them per-template.

export type QaVerdict = "PASS" | "FAIL";

export type QaMeta = {
  timestamp: string;
  skill: string;
  template_name: string;
  template_external_id?: string;
  licensee_original_file?: string;
  licensee_file_external_id?: string;
  document_type: string;
  version: number;
  cases?: string[];
  documents_screened?: {
    client: string;
    document_external_id: string;
    name?: string;
  }[];
  critical_alterations: number;
  hallucinations: number;
  unverified_files: number;
  verdict: QaVerdict;
  fixed_wording_verdict: QaVerdict;
  hallucination_verdict: QaVerdict;
};

export type TemplateQaReport = {
  templateName: string;
  templateExternalId?: string;
  version: number;
  timestamp: string;
  reportMd: string;
  meta: QaMeta;
};

// Eager glob imports run at build time. Each .md becomes a raw string; each
// .meta.json becomes a parsed object. Files that don't exist at build time
// produce no entries (zero cost).
const REPORT_MODULES = import.meta.glob<string>(
  "/outputs/template-qa/reports/*.md",
  { query: "?raw", import: "default", eager: true },
);
const META_MODULES = import.meta.glob<{ default: QaMeta }>(
  "/outputs/template-qa/*.meta.json",
  { eager: true },
);

const byTemplateName = new Map<string, TemplateQaReport>();
const byTemplateId = new Map<string, TemplateQaReport>();
const byDocId = new Map<string, TemplateQaReport>();

for (const [metaPath, metaModule] of Object.entries(META_MODULES)) {
  const meta = metaModule.default;
  if (!meta || !meta.template_name) continue;

  // Match the meta to its sibling report: same basename, .md instead of .meta.json
  const reportPath = metaPath.replace(/^\/outputs\/template-qa\//, "/outputs/template-qa/reports/").replace(/\.meta\.json$/, ".md");
  const reportMd = REPORT_MODULES[reportPath];
  if (!reportMd) continue;

  const entry: TemplateQaReport = {
    templateName: meta.template_name,
    templateExternalId: meta.template_external_id,
    version: meta.version,
    timestamp: meta.timestamp,
    reportMd,
    meta,
  };

  const existingByName = byTemplateName.get(meta.template_name);
  if (!existingByName || existingByName.version < meta.version) {
    byTemplateName.set(meta.template_name, entry);
  }

  if (meta.template_external_id) {
    const existingById = byTemplateId.get(meta.template_external_id);
    if (!existingById || existingById.version < meta.version) {
      byTemplateId.set(meta.template_external_id, entry);
    }
  }

  for (const doc of meta.documents_screened ?? []) {
    const existing = byDocId.get(doc.document_external_id);
    if (!existing || existing.version < meta.version) {
      byDocId.set(doc.document_external_id, entry);
    }
  }
}

export function getReportForDoc(docExternalId: string | null | undefined): TemplateQaReport | null {
  if (!docExternalId) return null;
  return byDocId.get(docExternalId) ?? null;
}

export function getReportForTemplate(opts: {
  templateExternalId?: string | null;
  templateName?: string | null;
}): TemplateQaReport | null {
  if (opts.templateExternalId && byTemplateId.has(opts.templateExternalId)) {
    return byTemplateId.get(opts.templateExternalId) ?? null;
  }
  if (opts.templateName && byTemplateName.has(opts.templateName)) {
    return byTemplateName.get(opts.templateName) ?? null;
  }
  return null;
}

export function findDocCase(
  report: TemplateQaReport,
  docExternalId: string,
): { client: string; name?: string } | null {
  return report.meta.documents_screened?.find((d) => d.document_external_id === docExternalId) ?? null;
}
